import React from 'react';
import { motion } from 'framer-motion';
import OptimizedHeroSection from '@/components/OptimizedHeroSection';
import TestimonialSection from '@/components/TestimonialSection';
import PersonaToggle from '@/components/PersonaToggle';
import AccountabilitySection from '@/components/AccountabilitySection';
import FeatureShowcase from '@/components/FeatureShowcase';
import AllInOnePlatform from '@/components/AllInOnePlatform';
import OutputGallery from '@/components/OutputGallery';
import MoatSection from '@/components/MoatSection';
import CompetitorComparison from '@/components/CompetitorComparison';
import SecurityFooter from '@/components/SecurityFooter';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CALENDLY_URL = 'https://calendly.com/airlius-hts/munger-ai-demo';

const handleBookCall = () => {
  window.open(CALENDLY_URL, '_blank');
};

const OptimizedIndex = () => {
  const navigate = useNavigate();



  // FAQ Section
  const FAQSection = () => (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about Munger.ai
          </p>
        </div>

        <div className="space-y-6">
          {[
            {
              q: "How quickly can I start seeing results?",
              a: "Most users see their first replies within 24-48 hours and book their first meeting within 3-5 days of going live."
            },
            {
              q: "Do I need to provide my own email lists?",
              a: "No! Our AI automatically finds and researches prospects based on your ideal customer profile. No lists required."
            },
            {
              q: "What happens on the call?",
              a: "We'll walk you through the platform, discuss your ideal customer profile, and show you exactly how Munger.ai can work for your business. No pressure, no obligations."
            },
            {
              q: "How is this different from other cold email tools?",
              a: "Other tools require manual setup, list building, and copywriting. Munger.ai is 100% automated - it finds prospects, writes emails, sends campaigns, and books meetings without any manual work."
            },
            {
              q: "Is my data secure?",
              a: "Yes. We use enterprise-grade security with SOC 2 compliance. Your data is encrypted and never shared with third parties."
            }
          ].map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {faq.q}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {faq.a}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Final CTA Section
  const FinalCTASection = () => (
    <section className="py-20 bg-blue-600">
      <div className="max-w-4xl mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Book Your First Meeting?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join 2,100+ companies using AI to scale their outreach.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={handleBookCall}
            >
              Book a Call
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-sm text-blue-100">
            <span>✓ 10-minute setup</span>
            <span>✓ First replies in 24 hours</span>
          </div>
        </motion.div>
      </div>
    </section>
  );

  return (
    <>
      <div className="bg-gradient-to-br from-white to-blue-50">
        <OptimizedHeroSection 
          onStartClick={handleBookCall} 
        />
        <TestimonialSection />
        <PersonaToggle />
        <AccountabilitySection />
        <FeatureShowcase />
        <AllInOnePlatform onStartClick={handleBookCall} />
        <OutputGallery />
        <MoatSection />
        <CompetitorComparison onStartClick={handleBookCall} />
        <FAQSection />
        <FinalCTASection />
        <SecurityFooter />
      </div>
    </>
  );
};

export default OptimizedIndex; 